import React, { useMemo, useState } from "react";
import { Box, Chip, Paper, Stack, Switch, Tooltip, Typography, useTheme } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { svSE, enUS } from "@mui/x-data-grid/locales";
import { useIntl } from "react-intl";
import { tokens } from "../../../assets/theme";
import { hasAnyRole } from "../../../auth/authUi";
import { useAuth } from "../../../auth/AuthContext";
import { CMMS_MANAGER_ROLES } from "../../cmms/cmmsRoles";
import {
  getAssetHierarchyPalette,
  getStatusChipSx,
  getSwitchSx,
  getTypeChipSx,
} from "../../cmms/assetHierarchyStyles";
import { CmmsListSkeleton } from "../../cmms/components/CmmsStates";
import messages from "../messages";

/*
 * Who receives CMMS work order mail.
 *
 * StorageLocationsDataGrid's shape, including its call not to wire up cmmsGridPrefs: five columns
 * on a settings tab have nothing to reorder, and those controls belong on the grids people
 * actually live in.
 *
 * The switch saves on change rather than through a Save button, the same pattern
 * RecurringSchedulesDataGrid uses for its active column. There is one field per row and no way to
 * get it half-right, so a form around it would be ceremony.
 *
 * The address column is not decoration. A user with no address never receives mail whatever the
 * switch says, and without this the admin turns one on, nothing happens, and there is nothing on
 * screen explaining why.
 *
 * The department column is the other half of that sentence. NotificationRecipientService reaches
 * technicians and admins through their department, so someone with an address, the switch on and no
 * department is unreachable by that route and nothing here would have said so.
 *
 * It is drawn as plainly as the address is, but it does not mean the same thing, and the tooltips
 * are where the two part company: no address stops mail on every route, while no department stops
 * one of three — a work order dispatched or assigned to that person by name still arrives.
 *
 * One caveat on an empty column, and it is the server's doing rather than this grid's: the
 * endpoint sends only the memberships in the caller's own company, while the mail resolver does not
 * filter by company at all. A membership in another tenant is therefore a route that reaches the
 * person and is not drawn here. It is the deliberate trade — a department name belongs to its
 * company — and it is why the column answers "what you can see and act on" rather than "every way
 * mail arrives".
 */
const ROLE_CHIP_COLORS = {
  4: { bg: "#00695C", text: "#fff" },
  1: { bg: "#B71C1C", text: "#fff" },
  2: { bg: "#1565C0", text: "#fff" },
  3: { bg: "#2E7D32", text: "#fff" },
};

export default function NotificationPreferencesDataGrid({
  preferences,
  isLoading,
  pendingUserIds,
  onToggle,
  emptyState,
}) {
  const theme = useTheme();
  const intl = useIntl();
  const colors = tokens(theme.palette.mode);
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const { roleIds } = useAuth();
  const language = localStorage.getItem("language") || "en";
  const translations = language === "sv" ? svSE : enUS;

  const [sortModel, setSortModel] = useState([{ field: "name", sort: "asc" }]);

  const canManage = hasAnyRole(roleIds, CMMS_MANAGER_ROLES);

  const columns = useMemo(() => [
    {
      field: "name",
      headerName: intl.formatMessage(messages.columnUser),
      flex: 1,
      minWidth: 180,
      // The name is optional on dbo.Users and plenty of rows created by first login have none,
      // so the username stands in rather than leaving an empty cell.
      valueGetter: (params) => params.row.name || params.row.username || `#${params.row.userId}`,
    },
    {
      field: "email",
      headerName: intl.formatMessage(messages.columnEmail),
      flex: 1,
      minWidth: 220,
      renderCell: (params) => {
        if (params.row.email) {
          return <Typography variant="body2">{params.row.email}</Typography>;
        }

        return (
          <Tooltip title={intl.formatMessage(messages.noEmailHint)}>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: cmmsColors.mutedText }}>
              <WarningAmberIcon fontSize="small" />
              <Typography variant="body2">
                {intl.formatMessage(messages.noEmail)}
              </Typography>
            </Stack>
          </Tooltip>
        );
      },
    },
    {
      field: "roleIds",
      headerName: intl.formatMessage(messages.columnRoles),
      flex: 1,
      minWidth: 160,
      sortable: false,
      renderCell: (params) => {
        const ids = (params.row.roleIds ?? []).slice().sort((a, b) => a - b);

        return (
          <Stack direction="row" useFlexGap flexWrap="wrap" gap={0.75} sx={{ width: "100%" }}>
            {ids.map((id) => {
              const color = ROLE_CHIP_COLORS[id] ?? { bg: "#e0e0e0", text: "#000" };
              return (
                <Chip
                  key={id}
                  size="small"
                  label={intl.formatMessage(messages[`role${id}`] ?? messages.roleUnknown, { id })}
                  sx={{ backgroundColor: color.bg, color: color.text }}
                />
              );
            })}
          </Stack>
        );
      },
    },
    {
      field: "departments",
      headerName: intl.formatMessage(messages.columnDepartments),
      flex: 1,
      minWidth: 200,
      sortable: false,
      renderCell: (params) => {
        const departments = params.row.departments ?? [];

        if (departments.length === 0) {
          return (
            <Tooltip title={intl.formatMessage(messages.noDepartmentHint)}>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: cmmsColors.mutedText }}>
                <WarningAmberIcon fontSize="small" />
                <Typography variant="body2">
                  {intl.formatMessage(messages.noDepartment)}
                </Typography>
              </Stack>
            </Tooltip>
          );
        }

        // Order comes from the server, which sorts inside the projection so chips cannot rearrange
        // between two reads of unchanged data. Sorting again here would be a second opinion.
        return (
          <Stack direction="row" useFlexGap flexWrap="wrap" gap={0.75} sx={{ width: "100%" }}>
            {departments.map((department) => {
              const chip = (
                <Chip
                  key={department.assetNodeId}
                  size="small"
                  label={department.name}
                  sx={department.isActive
                    ? getTypeChipSx(cmmsColors)
                    : getStatusChipSx(cmmsColors, false)}
                />
              );

              // Only the deactivated ones explain themselves; a tooltip on every chip saying
              // nothing would train people to ignore the one that says something.
              return department.isActive ? chip : (
                <Tooltip key={department.assetNodeId} title={intl.formatMessage(messages.departmentInactiveHint)}>
                  {chip}
                </Tooltip>
              );
            })}
          </Stack>
        );
      },
    },
    {
      field: "emailEnabled",
      headerName: intl.formatMessage(messages.columnEmailEnabled),
      width: 160,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        // stopPropagation so a click on the switch is not also a click on the row.
        <Box onClick={(event) => event.stopPropagation()}>
          <Switch
            size="small"
            checked={Boolean(params.row.emailEnabled)}
            // Disabled per row rather than behind a global backdrop: the rows are independent,
            // and freezing the whole grid because one switch is in flight would make a run of
            // changes feel broken.
            disabled={!canManage || pendingUserIds?.includes(params.row.userId)}
            onChange={() => onToggle?.(params.row)}
            inputProps={{ "aria-label": intl.formatMessage(messages.columnEmailEnabled) }}
            sx={getSwitchSx(cmmsColors)}
          />
        </Box>
      ),
    },
  ], [intl, cmmsColors, canManage, pendingUserIds, onToggle]);

  const gridSlots = useMemo(
    () => ({
      loadingOverlay: () => (
        <Box sx={{ px: 2, pt: 1.5, width: "100%" }}>
          <CmmsListSkeleton rows={6} height={36} />
        </Box>
      ),
      // Only when the caller supplied one — an overlay rendering `undefined` would cover the grid
      // with nothing.
      ...(emptyState ? {
        noRowsOverlay: () => (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
            {emptyState}
          </Box>
        ),
      } : {}),
    }),
    [emptyState],
  );

  return (
    <Paper sx={{ position: "relative", borderRadius: "4px" }}>
      <Box
        height="60vh"
        sx={{
          "& .MuiDataGrid-root": {
            backgroundColor: colors.primary[400],
            borderColor: `${colors.primary[700]} !important`,
            borderRadius: "0px",
            fontSize: "0.95rem",
            border: "0 !important",
          },
          "& .MuiDataGrid-cell": { borderColor: `${colors.primary[700]} !important` },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.primary[400],
            borderColor: `${colors.primary[700]} !important`,
          },
          "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.primary[600],
          },
        }}
      >
        <DataGridPro
          loading={isLoading}
          slots={gridSlots}
          localeText={translations.components.MuiDataGrid.defaultProps.localeText}
          rows={preferences || []}
          getRowId={(row) => row.userId}
          columns={columns}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          disableRowSelectionOnClick
        />
      </Box>
    </Paper>
  );
}
